import { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Avatar } from "primereact/avatar";
import { Chip } from "primereact/chip";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressBar } from "primereact/progressbar";
import { Fetch } from "../../../shared/Fetch";
// import { SendEmail } from "./SendEmail";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { safeFormatDate } from "../../../models/DateFormatMapper";
import { ILogin } from "../../../models/Login";
import * as pdfjsLib from "pdfjs-dist";
import mammoth from "mammoth";
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();

// ─── Monster API Types ────────────────────────────────────────────────────────

interface MonsterWorkAuthorization {
  authorization: string;
  country: string;
}

interface MonsterLocation {
  city: string;
  state: string;
  postalCode: string;
  country: string;
  willRelocate: boolean;
  workAuthorizations: MonsterWorkAuthorization[];
}

interface MonsterSkill {
  name: string;
  yearsOfExperience: number;
  lastUsed?: string;
  matched?: string;
}

interface MonsterRelevanceExperience {
  title?: { name: string };
  company?: { name: string };
}

interface MonsterRelevance {
  experience?: MonsterRelevanceExperience;
  skills?: MonsterSkill[];
}

interface MonsterBoard {
  id: number;
  name: string;
}

interface MonsterDesiredSalary {
  min: string | number;
  max: string | number;
}

interface MonsterPhoneNumber {
  phoneNumberValue: string;
  priority: string;
  type: string;
}

interface MonsterResumeDocument {
  fileName: string;
  fileContentType: string;
  file: string; // base64
}

interface MonsterCandidate {
  identity?: {
    textResumeID?: string;
    resumeModifiedDate?: string;
    md5EmailAddress?: string;
    emailAddress?: string;
    name?: string;
  };
  consultant_ID?: number;
  location?: MonsterLocation;
  yearsOfExperience?: number;
  relevance?: MonsterRelevance;
  boards?: MonsterBoard[];
  resumeTitle?: string;
  targetJobTitle?: string;
  desiredSalary?: MonsterDesiredSalary;
  phoneNumbers?: MonsterPhoneNumber[];
  highestEducationDegree?: string;
  resumeModifiedDate?: string;
  resume?: string; // HTML string
  resumeDocument?: MonsterResumeDocument;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getInitials = (name?: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
};

const timeSince = (dateStr?: string): string => {
  if (!dateStr) return "N/A";
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days > 30) return "30+ days ago";
  if (days === 0) return "Today";
  return `${days} day${days !== 1 ? "s" : ""} ago`;
};

const formatSalary = (salary?: MonsterDesiredSalary): string => {
  if (!salary || (!salary.min && !salary.max)) return "Not specified";
  const min = salary.min ? `$${Number(salary.min).toLocaleString()}` : "";
  const max = salary.max ? `$${Number(salary.max).toLocaleString()}` : "";
  if (min && max) return `${min} – ${max}`;
  return min || max;
};

const workAuthLabel = (location?: MonsterLocation): string => {
  const auths = location?.workAuthorizations;
  if (!auths || auths.length === 0) return "Not specified";
  const first = auths[0].authorization;
  if (first?.toLowerCase()?.includes("any employer")) return "Authorized (Any Employer)";
  return first;
};

const isValidBase64 = (str: string): boolean => {
  if (!str || str.length < 64) return false;
  const stripped = str.startsWith("data:") ? str.split(",")[1] : str;
  return /^[A-Za-z0-9+/\s]+=*$/.test(stripped.trim());
};

// ─── Shared UI (mirrored from CandidateProfile) ───────────────────────────────

const Section = ({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) => (
  <Card
    className="mb-3"
    style={{
      borderRadius: "10px",
      border: "1px solid #e5e9f0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    <div className="flex align-items-center gap-2 mb-3">
      <i className={icon} style={{ color: "#0d6efd", fontSize: "1rem" }} />
      <span style={{ fontWeight: 700, fontSize: "1rem", color: "#1a1d23" }}>{title}</span>
    </div>
    {children}
  </Card>
);

const LabelValue = ({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) => (
  <div className="mb-3">
    <div
      style={{
        fontSize: "0.7rem",
        fontWeight: 600,
        color: "#8392a5",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: "0.25rem",
      }}
    >
      {label}
    </div>
    {highlight ? (
      <Tag
        value={value as string}
        style={{
          background: "#e3f2fd",
          color: "#1565c0",
          fontWeight: 600,
          fontSize: "0.8rem",
        }}
      />
    ) : (
      <div style={{ fontWeight: 600, color: "#1a1d23", fontSize: "0.9rem" }}>{value}</div>
    )}
  </div>
);

// ─── Keyword Highlighter ──────────────────────────────────────────────────────

const BOOLEAN_OPERATORS = new Set(["and", "or", "not"]);

const parseKeywords = (keywords: string): string[] => {
  const terms: string[] = [];

  const quotedRegex = /"([^"]+)"/g;
  let match;
  while ((match = quotedRegex.exec(keywords)) !== null) {
    const phrase = match[1].trim();
    if (phrase.length > 0) terms.push(phrase);
  }

  const withoutQuotes = keywords.replace(/"[^"]*"/g, "");
  const singleWords = withoutQuotes
    .split(/[\s,]+/)
    .map((k) => k.replace(/^[(]+|[')]+$/g, "").trim())
    .filter((k) => k.length > 0 && !BOOLEAN_OPERATORS.has(k?.toLowerCase()));

  terms.push(...singleWords);
  return terms;
};

const termToPattern = (t: string): string => {
  const esc = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return `(?<!\\w)${esc}(?!\\w)`;
};

const highlightText = (text: string, keywords: string): React.ReactNode => {
  if (!keywords?.trim()) return text;
  const terms = parseKeywords(keywords);
  if (!terms.length) return text;

  const regex = new RegExp(`(${terms.map(termToPattern).join("|")})`, "gi");
  const parts = text.split(regex);
  const testRegex = new RegExp(`^(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})$`, "i");

  return parts.map((part, i) =>
    testRegex.test(part) ? (
      <mark key={i} style={{ background: "#fff176", color: "#1a1d23", borderRadius: "3px", padding: "0 2px", fontWeight: 700 }}>
        {part}
      </mark>
    ) : (
      part
    ),
  );
};

const highlightHtml = (html: string, keywords: string): string => {
  if (!keywords?.trim()) return html;
  const terms = parseKeywords(keywords);
  if (!terms.length) return html;
  const regex = new RegExp(`(${terms.map(termToPattern).join("|")})`, "gi");
  const markStyle = `background:#ffeb3b;color:#1a1d23;border-radius:3px;padding:1px 3px;font-weight:700;`;
  return html.replace(/>([^<]*)</g, (match, text) => {
    if (!text.trim()) return match;
    return `>${text.replace(regex, `<mark style="${markStyle}">$1</mark>`)}<`;
  });
};

// ─── Props ────────────────────────────────────────────────────────────────────

interface IProps {
  visible: boolean;
  singleData: any;
  toast: any;
  onLoaded?: () => void;
  usageData?: any;
  searchQuery?: string;
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MonsterCandidateProfile(props: IProps) {
  const [candidate, setCandidate] = useState<MonsterCandidate>({});
  const [note, setNote] = useState("");
  const [iframeSrc, setIframeSrc] = useState<string | undefined>(undefined);
  const [savedNotes, setSavedNotes] = useState<
    {
      comments?: string;
      user_Name?: string;
      createdOn?: Date;
      company_Name?: string;
      branchName?: string;
    }[]
  >([]);
  const [zoom, setZoom] = useState(100);
  // const [viewMode, setViewMode] = useState<"scroll" | "page">("scroll");
  // const [currentPage, setCurrentPage] = useState(1);
  const [sendEmail, setSendEmail] = useState({
    open: false,
    tableData: {} as any,
  });
  const [openViewHistory, setOpenViewHistory] = useState<{
    open: boolean;
    tableData: any[];
  }>({ open: false, tableData: [] });

  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  const DataFormat = loginObj?.date_Format;

  // ── Derived values ────────────────────────────────────────────────────────

  const fullName = candidate.identity?.name ?? "";
  const initials = getInitials(fullName);
  const email = candidate.identity?.emailAddress ?? "";
  const phone = candidate.phoneNumbers?.[0]?.phoneNumberValue ?? "";
  const loc = candidate.location;
  const currentTitle = candidate.relevance?.experience?.title?.name ?? "Not specified";
  const currentCompany = candidate.relevance?.experience?.company?.name ?? "";
  const skills = candidate.relevance?.skills ?? [];

  const highlightedResume = useMemo(() => highlightHtml(candidate.resume ?? "", props.searchQuery ?? ""), [candidate.resume, props.searchQuery]);

  const hasResume = (!!candidate.resumeDocument?.file && isValidBase64(candidate.resumeDocument.file)) || (!!candidate.resume && candidate.resume.trim().length > 0 && /<[a-z][\s\S]*>/i.test(candidate.resume));

  // ── API Calls ─────────────────────────────────────────────────────────────

  const loadData = async () => {
    setCandidate({});
    // Monster profile fetch — adjust endpoint/params to match your backend
    const response = await Fetch("Monster/getResume", "GET", null, `textResumeId=${props.singleData?.resumeID}`, props.toast);
    if (response) {
      const data: MonsterCandidate = await response.json();
      setCandidate(data);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const processFile = async () => {
      if (iframeSrc) {
        URL.revokeObjectURL(iframeSrc);
        setIframeSrc(undefined);
      }
      const resumeDoc = candidate.resumeDocument;
      const resumeHtml = candidate.resume;

      if (!candidate.identity?.textResumeID && !candidate.identity?.name) return;

      if (!resumeDoc?.file && !resumeHtml) {
        props.onLoaded?.();
        return;
      }

      // ─── 1. Use resume HTML if it's real HTML ──────────────────────────────
      if (resumeHtml && resumeHtml?.trim().length > 0 && resumeHtml?.trim()?.toLowerCase() !== "updated html" && /<[a-z][\s\S]*>/i.test(resumeHtml)) {
        const html = `<html><head><style>
                      body { font-family: Arial, sans-serif; padding: 24px; max-width: 900px; margin: auto; line-height: 1.6; font-size: 0.9rem; color: #374151; }
                      mark { background: #fff176; color: #1a1d23; border-radius: 3px; padding: 0 2px; font-weight: 600; }
                      </style></head><body>${highlightHtml(resumeHtml, props.searchQuery ?? "")}</body></html>`;

        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        if (isCancelled) return;
        setIframeSrc(URL.createObjectURL(blob));
        props.onLoaded?.();
        return;
      }

      // ─── 2. Validate base64 ────────────────────────────────────────────────
      if (!resumeDoc?.file || !isValidBase64(resumeDoc.file)) {
        console.warn("resumeDocument.file is not valid base64 — got:", resumeDoc?.file);
        setIframeSrc(undefined);
        props.onLoaded?.();
        return;
      }

      // ─── 3. Decode base64 → bytes ──────────────────────────────────────────
      const base64 = resumeDoc.file.startsWith("data:") ? resumeDoc.file.split(",")[1] : resumeDoc.file;
      let bytes: Uint8Array;
      try {
        const binary = atob(base64);
        bytes = new Uint8Array(new ArrayBuffer(binary.length));
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      } catch (decodeErr) {
        console.error("Failed to decode base64:", decodeErr);
        setIframeSrc(undefined);
        props.onLoaded?.();
        return;
      }

      if (isCancelled) return;

      const contentType = resumeDoc.fileContentType || "";
      const filename = resumeDoc.fileName?.toLowerCase() || "";

      // ─── 4a. DOCX ──────────────────────────────────────────────────────────
      if (contentType.includes("wordprocessingml") || contentType.includes("msword") || filename.endsWith(".docx")) {
        try {
          // Copy into a clean ArrayBuffer — mammoth rejects shared/offset buffers
          const cleanBuffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
          const result = await mammoth.convertToHtml({ arrayBuffer: cleanBuffer });

          const html = `<html><head><style>
                        body { font-family: Arial, sans-serif; padding: 24px; max-width: 900px; margin: auto; line-height: 1.6; }
                        mark { background: #fff176; color: #1a1d23; border-radius: 3px; padding: 0 2px; font-weight: 600; }
                        </style></head><body>${highlightHtml(result.value, props.searchQuery ?? "")}</body></html>`;

          const blob = new Blob([html], { type: "text/html;charset=utf-8" });
          if (isCancelled) return;
          setIframeSrc(URL.createObjectURL(blob));
          props.onLoaded?.();
        } catch (docxErr) {
          console.error("Failed to parse DOCX:", docxErr);
          // Fallback: offer the raw file as a download blob
          const fallbackBlob = new Blob([""], { type: contentType });
          if (isCancelled) return;
          setIframeSrc(URL.createObjectURL(fallbackBlob));
          props.onLoaded?.();
        }
        return;
      }

      // ─── 4b. Plain text / RTF ──────────────────────────────────────────────
      if (contentType.includes("text/plain") || contentType.includes("text/rtf") || filename.endsWith(".txt") || filename.endsWith(".rtf")) {
        const text = new TextDecoder("utf-8").decode(bytes);
        const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const html = `<html><head><style>
          body { font-family: monospace; padding: 24px; background: #fff; white-space: pre-wrap; word-break: break-word; }
  </style></head><body>${escaped}</body></html>`;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        if (isCancelled) return;
        setIframeSrc(URL.createObjectURL(blob));
        props.onLoaded?.();
        return;
      }

      // ─── 4c. Images ────────────────────────────────────────────────────────
      if (contentType.startsWith("image/")) {
        const base64Img = btoa(
          Array.from(bytes)
            .map((b) => String.fromCharCode(b))
            .join(""),
        );
        const dataUrl = `data:${contentType};base64,${base64Img}`;
        const html = `<html><head><style>
          body { margin:0; background:#f4f4f4; display:flex; justify-content:center; padding:16px; }
          img { max-width:100%; box-shadow:0 2px 8px rgba(0,0,0,0.2); border-radius:4px; }
  </style></head><body><img src="${dataUrl}" /></body></html>`;
        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        if (isCancelled) return;
        setIframeSrc(URL.createObjectURL(blob));
        props.onLoaded?.();
        return;
      }

      // ─── 4d. PDF (default) ─────────────────────────────────────────────────
      let pdf: pdfjsLib.PDFDocumentProxy;
      try {
        pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
      } catch (pdfErr) {
        console.error("Failed to parse PDF:", pdfErr);
        setIframeSrc(undefined);
        props.onLoaded?.();
        return;
      }

      let pagesHtml = `<html><head><style>
        body { margin:0; padding:16px; background:#f4f4f4; }
        .page-wrap { position:relative; background:#fff; margin-bottom:16px;
          box-shadow:0 2px 8px rgba(0,0,0,0.15); border-radius:4px; overflow:hidden; display:inline-block; }
        img { display:block; }
  </style></head><body>`;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const scale = 1.8;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d")!;
        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        if (isCancelled) return;
        const dataUrl = canvas.toDataURL("image/png");
        pagesHtml += `
  <div class="page-wrap" style="width:${viewport.width}px;">
  <img src="${dataUrl}" width="${viewport.width}" height="${viewport.height}" />
  </div>`;
      }

      pagesHtml += `</body></html>`;
      const blob = new Blob([pagesHtml], { type: "text/html;charset=utf-8" });
      if (isCancelled) return;
      setIframeSrc(URL.createObjectURL(blob));
      props.onLoaded?.();
      return;
    };
    // props.onLoaded?.();
    processFile();
    return () => {
      isCancelled = true;
    };
  }, [candidate.resumeDocument?.file, candidate.resume]);

  const saveCandidateNote = async () => {
    if (note.trim() === "" || !props.singleData?.resumeID) return;
    const body = {
      resumeID: props.singleData.resumeID,
      comments: note,
    };
    const response = await Fetch("Consultant_Mast/addEditConsultantNote", "POST", body, null, props.toast);
    if (response) {
      props.toast.current.show({
        severity: "success",
        detail: "Comment Added Successfully",
        life: 3000,
      });
      setNote("");
      getCandidateNotes();
    }
  };

  const getCandidateNotes = async () => {
    const response = await Fetch("MassMail/getCandidateNote", "GET", null, `resumeID=${props.singleData?.resumeID}`, props.toast);
    if (response) {
      const data: any[] = await response.json();
      setSavedNotes(data);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    props.toast.current.show({
      severity: "success",
      detail: "Copied Resume ID",
      life: 3000,
    });
  };

  useEffect(() => {
    if (props.visible && props.singleData?.resumeID) {
      loadData();
    }
  }, [props.visible, props.singleData?.resumeID]);

  // useEffect(() => {
  //   if (candidate.identity?.textResumeID) {
  //     getCandidateNotes();
  //   }
  // }, [candidate]);

  // ── Render ────────────────────────────────────────────────────────────────

  //   useEffect(() => {
  //   if (props.singleData?.resumeID) {
  //     loadData();
  //   }
  // }, [props.visible, props.singleData?.resumeID]);

  return (
    <>
      <div style={{ background: "#f0e0fa", fontFamily: "'Inter', sans-serif", minHeight: "100%", paddingTop: "5px" }}>
        {/* ── Header Card ── */}
        <Card className="mb-3" style={{ borderRadius: "10px", border: "1px solid #e5e9f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", marginTop: "10px", marginLeft: "12px", marginRight: "12px" }}>
          <div className="flex align-items-start gap-3 flex-wrap">
            <Avatar
              label={initials}
              shape="circle"
              style={{
                width: "64px",
                height: "64px",
                fontSize: "1.4rem",
                fontWeight: 700,
                flexShrink: 0,
                background: "linear-gradient(135deg, #8bfff7, #07a999)",
                color: "#fff"
              }}
            />

            <div style={{ flex: 1, minWidth: "200px" }}>
              {/* Name + Resume ID */}
              <div
                style={{ fontSize: "1.35rem", fontWeight: 700, color: "#1a1d23", lineHeight: 1.2 }}
              >
                {highlightText(fullName || "—", props.searchQuery ?? "")}{" "}
                {candidate.consultant_ID && (
                  <b
                    style={{
                      border: "1px solid rgb(229, 233, 240)",
                      borderRadius: "10%",
                      padding: "5px",
                      cursor: "pointer",
                      fontSize: "0.85rem"
                    }}
                    onClick={() => copyToClipboard(`${candidate?.consultant_ID}` || "")}
                  >
                    {candidate.consultant_ID} <span className="pi pi-clone" />
                  </b>
                )}
              </div>

              {/* Monster source badge */}
              <div
                style={{
                  marginTop: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#fff3e0",
                  border: "1px solid #ffe0b2",
                  borderRadius: "6px",
                  padding: "2px 10px",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#e65100"
                }}
              >
                <span>🟠</span> Monster Profile
              </div>

              {/* Title @ Company */}
              <div className="flex align-items-center gap-2 mt-2 flex-wrap">
                <span
                  style={{
                    background: "#fff3cd",
                    color: "#856404",
                    padding: "2px 10px",
                    borderRadius: "6px",
                    fontWeight: 600,
                    fontSize: "0.82rem"
                  }}
                >
                  {highlightText(currentTitle, props.searchQuery ?? "")}
                </span>
                {(currentCompany !== undefined && currentCompany) ? <span style={{ color: "#6c757d", fontSize: "0.88rem" }}>@ {currentCompany}</span> : <></>}
              </div>

              {/* Meta chips */}
              <div className="flex gap-2 mt-2 flex-wrap">
                {(loc?.city || loc?.state) && (
                  <Chip
                    label={`📍 ${[loc?.city, loc?.state, loc?.country].filter(Boolean).join(", ")}`}
                    style={{
                      fontSize: "0.78rem",
                      background: "#f0f4ff",
                      color: "#0d6efd"
                    }}
                  />
                )}
                {loc?.willRelocate !== undefined && (
                  <Chip
                    label={loc.willRelocate ? "🚗 Open to Relocation" : "📌 No Relocation"}
                    style={{
                      fontSize: "0.78rem",
                      background: loc.willRelocate ? "#e8f5e9" : "#fce4ec",
                      color: loc.willRelocate ? "#2e7d32" : "#b71c1c"
                    }}
                  />
                )}
                {candidate.yearsOfExperience !== undefined && (
                  <Chip
                    label={`🎓 ${candidate.yearsOfExperience} yrs exp`}
                    style={{
                      fontSize: "0.78rem",
                      background: "#f3e5f5",
                      color: "#7b1fa2"
                    }}
                  />
                )}
                <Chip
                  label={workAuthLabel(loc)}
                  style={{
                    fontSize: "0.78rem",
                    background: "#e3f2fd",
                    color: "#1565c0"
                  }}
                />
                {candidate.highestEducationDegree && (
                  <Chip
                    label={`📚 ${candidate.highestEducationDegree}`}
                    style={{
                      fontSize: "0.78rem",
                      background: "#f1f8e9",
                      color: "#33691e"
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* ── Two-column layout ── */}
        <div className="grid" style={{ margin: 0 }}>
          {/* LEFT COLUMN */}
          <div className="col-12 lg:col-8" style={{ paddingRight: "0.75rem" }}>
            {/* HIGHLIGHTS */}
            <Section title="Highlights" icon="pi pi-star">
              <div className="grid" style={{ margin: 0 }}>
                <div className="col-6 md:col-4">
                  <LabelValue label="Years of Experience" value={`${candidate.yearsOfExperience ?? "—"} years`} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Highest Education" value={candidate.highestEducationDegree ?? "—"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Location" value={[loc?.city, loc?.state, loc?.postalCode, loc?.country].filter(Boolean).join(", ") || "—"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue
                    label="Willing to Relocate"
                    value={
                      <Tag
                        value={loc?.willRelocate ? "Yes" : "No"}
                        style={{
                          background: loc?.willRelocate ? "#e8f5e9" : "#fff3e0",
                          color: loc?.willRelocate ? "#2e7d32" : "#e65100",
                          fontWeight: 600,
                          fontSize: "0.78rem"
                        }}
                      />
                    }
                  />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Target Job Title" value={candidate.targetJobTitle || "Not specified"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Work Authorization" value={workAuthLabel(loc)} highlight />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Desired Salary" value={formatSalary(candidate.desiredSalary)} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Source Board" value={candidate.boards?.map((b) => b.name).join(", ") || "—"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Resume Title" value={candidate.resumeTitle || "—"} />
                </div>
              </div>
            </Section>

            {/* SKILLS */}
            <Section title="Skills" icon="pi pi-code">
              {skills.length === 0 ? (
                <span style={{ color: "#8392a5", fontSize: "0.85rem" }}>No skills listed.</span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    margin: 0,
                    gap: "0.4rem"
                  }}
                >
                  {skills.map(({ name, yearsOfExperience, lastUsed, matched }) => (
                    <div
                      key={name}
                      style={{
                        flex: "1 1 calc(33% - 0.4rem)",
                        minWidth: "140px",
                        maxWidth: "100%",
                        background: matched ? "#f0fff4" : "#f8f9fb",
                        borderRadius: "8px",
                        padding: "0.5rem 0.75rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: `1px solid ${matched ? "#b2dfdb" : "#e5e9f0"}`,
                        gap: "0.4rem",
                        boxSizing: "border-box",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#1a1d23",
                          fontSize: "0.82rem",
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {highlightText(name, props.searchQuery ?? "")}
                      </span>
                      <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                        <Tag
                          value={`${yearsOfExperience?.toFixed(1) ?? "—"}yr`}
                          style={{
                            background: "#e3f2fd",
                            color: "#1565c0",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                          }}
                        />
                        {lastUsed && (
                          <Tag
                            value={new Date(lastUsed).getFullYear().toString()}
                            style={{
                              background: "#f3e5f5",
                              color: "#7b1fa2",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          />
                        )}
                        {matched !== undefined && matched !== "" && (
                          <Tag
                            value="✓"
                            style={{
                              background: "#e8f5e9",
                              color: "#2e7d32",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* WORK PREFERENCES */}
            <Section title="Work Preferences" icon="pi pi-briefcase">
              <div className="grid" style={{ margin: 0 }}>
                <div className="col-6 md:col-4">
                  <LabelValue label="Willing to Relocate" value={loc?.willRelocate ? "Yes" : "No"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Desired Min Salary" value={candidate.desiredSalary?.min ? `$${Number(candidate.desiredSalary.min).toLocaleString()}` : "—"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Desired Max Salary" value={candidate.desiredSalary?.max ? `$${Number(candidate.desiredSalary.max).toLocaleString()}` : "—"} />
                </div>
                <div className="col-6 md:col-4">
                  <LabelValue label="Target Job Title" value={candidate.targetJobTitle || "—"} />
                </div>
              </div>

              <Divider style={{ margin: "1rem 0 0.75rem" }} />
              <div
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "#8392a5",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: "0.5rem"
                }}
              >
                Work Authorizations
              </div>
              <div className="flex flex-wrap gap-2">
                {loc?.workAuthorizations?.map((auth, i) => (
                  <Chip
                    key={i}
                    label={`${auth.country}: ${auth.authorization}`}
                    style={{
                      fontSize: "0.78rem",
                      background: "#f0f4ff",
                      color: "#0d6efd",
                    }}
                  />
                ))}
              </div>
            </Section>

            {/* RESUME HTML PREVIEW */}
            {!hasResume && (
              <Section title="Resume Preview" icon="pi pi-file">
                <div
                  className="border-1 border-round surface-border"
                  style={{
                    maxHeight: "500px",
                    overflowY: "auto",
                    padding: "16px",
                    background: "#fff",
                    fontSize: "0.82rem",
                    color: "#374151",
                    lineHeight: 1.6,
                    borderRadius: "8px",
                    border: "1px solid #e5e9f0"
                  }}
                >
                  {candidate.resume ? (
                    <div dangerouslySetInnerHTML={{ __html: highlightedResume }} />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        color: "#8392a5",
                        padding: "40px 0",
                      }}
                    >
                      <i className="pi pi-file" style={{ fontSize: "2rem", display: "block", marginBottom: "8px" }} />
                      No resume preview available.
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* PDF RESUME */}
            <Section title="Resume Document" icon="pi pi-file-pdf">
              <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                {/* Zoom Controls */}
                <div className="flex align-items-center gap-2">
                  <Button icon="pi pi-minus" disabled={!hasResume || zoom <= 50} onClick={() => setZoom((z: any) => Math.max(50, z - 10))} tooltip="Zoom out" tooltipOptions={{ position: "bottom" }} />
                  <Button label={`${zoom}%`} disabled={!hasResume} onClick={() => setZoom(100)} tooltip="Reset zoom" tooltipOptions={{ position: "bottom" }} style={{ minWidth: "4rem", fontWeight: 600 }} />
                  <Button icon="pi pi-plus" disabled={!hasResume || zoom >= 200} onClick={() => setZoom((z) => Math.min(200, z + 10))} tooltip="Zoom in" tooltipOptions={{ position: "bottom" }} />
                </div>

                {/* Download */}
                <Button
                  icon="pi pi-download"
                  label="Download"
                  className="p-button-sm p-button-success"
                  disabled={!hasResume}
                  onClick={() => {
                    if (!candidate.resumeDocument?.file) return;
                    try {
                      const fileData = candidate?.resumeDocument?.file || "";
                      const base64 = fileData.startsWith("data:") ? fileData.split(",")[1] : fileData;
                      const binary = atob(base64);
                      const bytes = new Uint8Array(binary.length);
                      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                      const blob = new Blob([bytes], { type: "application/pdf" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = candidate.resumeDocument.fileName || "resume.pdf";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error("Download failed", err);
                    }
                  }}
                />
              </div>

              <div
                style={{
                  height: "600px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  background: hasResume ? "#f4f4f4" : "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e5e9f0",
                  padding: hasResume ? "12px 0" : 0
                }}
              >
                {hasResume ? (
                  <iframe
                    title="resume-pdf"
                    src={iframeSrc}
                    style={{
                      border: "none",
                      width: `${zoom}%`,
                      minWidth: "280px",
                      height: "100%",
                      minHeight: "3000px",
                      display: "block",
                      margin: "0 auto",
                      transition: "width 0.2s",
                      overflow: "hidden",
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-column align-items-center justify-content-center gap-2 text-color-secondary">
                    <i className="pi pi-file-pdf" style={{ fontSize: "2.5rem", color: "var(--surface-400)" }} />
                    <span className="text-sm">No Resume Available</span>
                  </div>
                )}
              </div>
            </Section>
          </div>

          {/* RIGHT COLUMN */}
          <div className="col-12 lg:col-4">
            {/* CONTACT */}
            <Section title="Contact" icon="pi pi-phone">
              <div className="flex gap-2 flex-wrap mb-2">
                {email && (
                  <Button
                    icon="pi pi-envelope"
                    label="Email"
                    onClick={() => setSendEmail({ open: true, tableData: candidate })}
                    style={{
                      flexGrow: 1,
                      minWidth: "130px",
                      border: "1px solid #0d6efd",
                      color: "white",
                      fontWeight: 600,
                      borderRadius: "8px",
                      justifyContent: "center"
                    }}
                  />
                )}
              </div>
              {email && (
                <div className="mt-2">
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#495057",
                      marginBottom: "0.2rem",
                    }}
                  >
                    <i className="pi pi-envelope mr-1" style={{ color: "#8392a5", fontSize: "0.75rem" }} />
                    {email}
                  </div>
                </div>
              )}
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "#8392a5",
                  marginTop: "0.5rem",
                }}
              >
                {phone ? `📞 ${phone}` : "📵 No phone on file"}
              </div>

              {/* All phone numbers */}
              {(candidate.phoneNumbers?.length ?? 0) > 1 && (
                <div style={{ marginTop: "0.5rem" }}>
                  {candidate?.phoneNumbers?.slice(1).map((p, i) => (
                    <div
                      key={i}
                      style={{
                        fontSize: "0.8rem",
                        color: "#495057",
                        marginBottom: "0.2rem",
                      }}
                    >
                      <i className="pi pi-phone mr-1" style={{ color: "#8392a5", fontSize: "0.75rem" }} />
                      {p.phoneNumberValue}{" "}
                      <span style={{ color: "#8392a5" }}>
                        ({p.type}, {p.priority})
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* CANDIDATE ACTIVITY */}
            <Section title="Candidate Activity" icon="pi pi-clock">
              {[
                {
                  label: "Resume Last Modified",
                  date: candidate.identity?.resumeModifiedDate,
                },
                {
                  label: "Resume Modified (Root)",
                  date: candidate.resumeModifiedDate,
                },
              ].map(({ label, date }) => (
                <div key={label} style={{ marginBottom: "0.9rem" }}>
                  <div
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "#8392a5",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "0.2rem"
                    }}
                  >
                    {label}
                  </div>
                  <div className="flex align-items-center gap-1" style={{ fontSize: "0.85rem", color: "#495057" }}>
                    <i className="pi pi-clock" style={{ color: "#8392a5", fontSize: "0.75rem" }} />
                    {date ? timeSince(date) : "N/A"}
                  </div>
                  {date && (
                    <div style={{ fontSize: "0.75rem", color: "#8392a5" }}>
                      {new Date(date).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  )}
                </div>
              ))}
            </Section>

            {/* PROFILE SOURCE */}
            <Section title="Profile Source" icon="pi pi-database">
              {candidate.boards?.map((board) => (
                <div key={board.id} className="flex align-items-center gap-2 mb-2">
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "6px",
                      background: "#e8580c",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: "0.72rem",
                      }}
                    >
                      M
                    </span>
                  </div>
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#1a1d23",
                      fontSize: "0.85rem"
                    }}
                  >
                    {board.name} Profile
                  </span>
                </div>
              ))}
              {candidate.identity?.textResumeID && (
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#8392a5",
                    marginTop: "0.4rem",
                  }}
                >
                  Resume ID: <b style={{ color: "#495057" }}>{candidate.identity.textResumeID}</b>
                </div>
              )}
            </Section>

            {/* EMPLOYER ACTIVITY */}
            <Section title="Employer Activity" icon="pi pi-eye">
              <div style={{ marginBottom: "0.9rem" }}>
                <div
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "#8392a5",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "0.2rem",
                  }}
                >
                  Last Viewed
                </div>
                <div style={{ fontSize: "0.85rem", color: "#495057" }}>
                  <i className="pi pi-eye mr-1" style={{ color: "#8392a5", fontSize: "0.75rem" }} />
                  {new Date().toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            </Section>

            {/* NOTES */}
            <Section title="Notes" icon="pi pi-pencil">
              <Button icon="pi pi-eye" label="View Notes" style={{ backgroundColor: "white", color: "black" }} onClick={() => setOpenViewHistory({ open: true, tableData: savedNotes })} />
              <InputTextarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter notes and click save"
                rows={4}
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  border: "1px solid #dee2e6",
                  resize: "vertical",
                  marginTop: "1rem",
                }}
              />
              <div className="flex justify-content-end mt-2">
                <Button
                  label="Save"
                  icon="pi pi-save"
                  style={{
                    background: "#0d6efd",
                    border: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                  }}
                  onClick={saveCandidateNote}
                />
              </div>
            </Section>

            {/* SIMILAR CANDIDATES */}
            <Section title="Similar Candidates" icon="pi pi-users">
              <div className="text-center" style={{ padding: "1rem 0", color: "#8392a5" }}>
                <i
                  className="pi pi-search"
                  style={{
                    fontSize: "1.8rem",
                    marginBottom: "0.5rem",
                    display: "block",
                  }}
                />
                <span style={{ fontSize: "0.85rem" }}>Browse similar candidates in TalentSearch</span>
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Send Email Dialog */}
      {/* <SendEmail open={sendEmail.open} tableData={[sendEmail.tableData]} onClose={() => setSendEmail({ open: false, tableData: {} })} toast={props.toast} /> */}

      {/* Notes History Dialog */}
      <Dialog header="Candidate Note's History" visible={openViewHistory.open} style={{ width: "50vw" }} modal onHide={() => setOpenViewHistory({ open: false, tableData: [] })}>
        <DataTable style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0" }} size="small" value={openViewHistory.tableData} emptyMessage="No Data found.">
          <Column field="user_Name" header="User Name" />
          <Column field="company_Name" header="Company" />
          <Column field="branchName" header="Branch" />
          <Column field="comments" header="Comment" />
          <Column field="createdOn" header="Searched On" body={(rowData: any) => safeFormatDate(rowData.createdOn, "dd/MM/yyyy", DataFormat)} />
        </DataTable>
      </Dialog>
    </>
  );
}
